import React from 'react';
import { Moment } from 'moment';
import {
	useMutation,
	useQuery,
	useQueryClient,
} from 'react-query';
import { SyncOutlined } from '@ant-design/icons';
import {
	Currencies,
	DATE,
	FeatureFlags,
} from '@shared/utils/constants';
import { standardSort } from '@shared/utils/standardSort';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import { VoyageExpensePaymentProps } from '@api/models/voyage-expense-payment';
import { formatDate } from '@client/utils/formatDate';
import {
	createVoyageExpensePayment,
	deleteVoyageExpensePayment,
	getVoyageExpensePayments,
	syncExpensePayments,
	updateVoyageExpensePayment,
} from '@client/lib/api';
import styles from '@client/screens/fleet/VoyageDetailsScreen/components/styles/PaymentsTable.module.css';
import EditableTable from '@client/components/EditableTable';
import showErrorNotification from '@client/utils/showErrorNotification';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import Button from '@client/components/Button';

type ExpensePaymentsTableType = {
	expenseId: number;
	currency?: Values<typeof Currencies>;
	refreshExpenses: () => void;
};

const ExpensePaymentsTable = ({
	expenseId,
	currency,
	refreshExpenses,
}: ExpensePaymentsTableType) => {
	const usesPaymentsSync = useFeatureFlag(FeatureFlags.PAYMENTS_SYNC);
	const queryClient = useQueryClient();

	const invalidate = () => queryClient.invalidateQueries(['payments', expenseId]);

	const createVoyageExpensePaymentMutation = useMutation(
		createVoyageExpensePayment,
		{
			onSuccess: () => {
				invalidate();
				refreshExpenses();
			},
		},
	);
	const createPayment = (payment: VoyageExpensePaymentProps) => {
		createVoyageExpensePaymentMutation.mutate({
			expenseId,
			paymentDate: payment.paymentDate,
			amount: payment.amount,
		});
	};

	const updateVoyageExpensePaymentMutation = useMutation(
		updateVoyageExpensePayment,
		{
			onSuccess: () => {
				invalidate();
				refreshExpenses();
			},
		},
	);
	const updatePayment = (id: number, payment: VoyageExpensePaymentProps) => {
		updateVoyageExpensePaymentMutation.mutate({
			attributes: { ...payment },
			id,
		});
	};

	const deleteVoyageExpensePaymentMutation = useMutation(
		deleteVoyageExpensePayment,
		{
			onSuccess: () => {
				invalidate();
				refreshExpenses();
			},
		},
	);
	const deletePayment = (id: number) => {
		deleteVoyageExpensePaymentMutation.mutate({
			id,
		});
	};

	const {
		data: payments,
	} = useQuery(
		['payments', expenseId],
		() => getVoyageExpensePayments(expenseId),
		{
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const columns = [
		{
			title: 'Date',
			dataIndex: 'paymentDate',
			editable: true,
			sorter: standardSort('paymentDate'),
			defaultSortOrder: 'desc',
			editingProps: {
				type: 'date',
			},
			render: (date: Moment) => formatDate(date, DATE),
		},
		{
			align: 'right' as const,
			title: 'Amount',
			dataIndex: 'amount',
			editable: true,
			editingProps: {
				type: 'currency',
			},
			render: (v: string) => formatCurrency(v ?? 0, currency ?? Currencies.USD),
		},
	];

	const onSync = async () => {
		try {
			await syncExpensePayments(expenseId);
			invalidate();
			refreshExpenses();
		} catch (e) {
			showErrorNotification('Could not sync payments', e as Error);
		}
	};

	return (
		<>
			{usesPaymentsSync && (
				<Button
					style={{ marginRight: 8 }}
					onClick={onSync} icon={(<SyncOutlined />)}>
					Sync payments
				</Button>
			)}
			<EditableTable<VoyageExpensePaymentProps, 'id'>
				className={styles.paymentTable}
				iconButtons
				addNewText="Payment(s)"
				emptyText="No payments"
				allowAddNew
				enableDelete
				onAddNew={createPayment}
				onSave={updatePayment}
				onDelete={deletePayment}
				keyDataIndex="id"
				// @ts-ignore
				columns={columns}
				pagination={false}
				dataSource={payments ?? []}
				rowKey="id"
				size="small"
			/>
		</>
	);
};

export default ExpensePaymentsTable;
